<template>
  <div>
    <input
      :id="id"
      type="date"
      class="form-control"
      :value="inputValue"
      :min="minValue"
      :max="maxValue"
      :disabled="disabled"
      :required="required"
      @input="updateValue($event)"
    >
    <button
      v-if="resetButton && inputValue !== ''"
      type="button"
      class="btn btn-sm mt-2"
      :class="`btn-${resetButtonVariant || 'outline-secondary'}`"
      @click="clearValue()"
    >
      {{ labelResetButton || 'Clear' }}
    </button>
  </div>
</template>

<script>
const YMD_REGEX = /^(\d{4})-(\d{2})-(\d{2})$/;

function formatYMD(date) {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function parseYMD(ymd) {
  const match = YMD_REGEX.exec(ymd);
  if (!match) {
    return null;
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day);

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date;
}

export default {
  name: 'BFormDatepicker',
  props: {
    modelValue: {
      type: [Date, String, Number],
      default: null,
    },
    valueAsDate: {
      type: Boolean,
      default: false,
    },
    dateDisabledFn: {
      type: Function,
      default: null,
    },
    resetButton: {
      type: Boolean,
      default: false,
    },
    resetButtonVariant: {
      type: String,
      default: 'outline-secondary',
    },
    labelResetButton: {
      type: String,
      default: 'Clear',
    },
    min: {
      type: [Date, String, Number],
      default: null,
    },
    max: {
      type: [Date, String, Number],
      default: null,
    },
    id: {
      type: String,
      default: undefined,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
    // Props used in existing templates to preserve compatibility.
    locale: {
      type: String,
      default: undefined,
    },
    hideHeader: {
      type: Boolean,
      default: false,
    },
    noHighlightToday: {
      type: Boolean,
      default: false,
    },
    menuClass: {
      type: String,
      default: undefined,
    },
    labelHelp: {
      type: String,
      default: undefined,
    },
  },
  emits: ['update:modelValue'],
  computed: {
    inputValue() {
      const date = this.parseInputDate(this.modelValue);
      return date ? formatYMD(date) : '';
    },
    minValue() {
      const date = this.parseInputDate(this.min);
      return date ? formatYMD(date) : undefined;
    },
    maxValue() {
      const date = this.parseInputDate(this.max);
      return date ? formatYMD(date) : undefined;
    },
  },
  methods: {
    parseInputDate(value) {
      if (value === null || value === undefined || value === '') {
        return null;
      }

      if (value instanceof Date) {
        if (Number.isNaN(value.getTime())) {
          return null;
        }
        return value;
      }

      if (typeof value === 'number') {
        const parsedFromNumber = new Date(value);
        if (Number.isNaN(parsedFromNumber.getTime())) {
          return null;
        }
        return parsedFromNumber;
      }

      if (typeof value === 'string') {
        const ymdPart = value.substring(0, 10);
        const fromYMD = parseYMD(ymdPart);
        if (fromYMD) {
          return fromYMD;
        }
        const parsedFromString = new Date(value);
        if (Number.isNaN(parsedFromString.getTime())) {
          return null;
        }
        return parsedFromString;
      }

      return null;
    },
    isDateDisabled(date) {
      if (!this.dateDisabledFn) {
        return false;
      }
      return this.dateDisabledFn(formatYMD(date), date) === true;
    },
    updateValue(event) {
      const nextYMD = event.target.value;
      if (!nextYMD) {
        this.$emit('update:modelValue', null);
        return;
      }

      const nextDate = parseYMD(nextYMD);
      if (!nextDate) {
        const { target } = event;
        if (target) {
          target.value = this.inputValue;
        }
        return;
      }

      if (this.isDateDisabled(nextDate)) {
        const { target } = event;
        if (target) {
          target.value = this.inputValue;
        }
        return;
      }

      this.$emit('update:modelValue', this.valueAsDate ? nextDate : nextYMD);
    },
    clearValue() {
      this.$emit('update:modelValue', null);
    },
  },
};
</script>
